import axios from "axios";
import { encode } from "blurhash";
import { getColorSync } from "colorthief";
import { uploadImage, uploadVideo } from "deso-protocol";
import type { Dispatch, SetStateAction } from "react";
import * as tus from "tus-js-client";

import { retry } from "@/backend/api-helpers";
import type { EditorMediaType } from "@/components/app/editor-v3/constants";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import {
  DEFAULT_VIDEO_PREVIEW_DIMENSIONS,
  hasValidMediaDimensions,
  isValidMediaDimension,
  normalizeMediaDimension,
} from "@/utils/media-dimensions";

export interface MediaItem {
  src: string;
  type: EditorMediaType;
  file: File;
  // this is only relevant for videos and audios
  duration?: number;
  // These are optional, UI will be set based on the existence of these props
  progress?: number | null;
  // Optional, custom metadata you may attach to the item
  metadata?: ImageMetadata | VideoMetadata | AudioMetadata;
  id: string;

  uploadFailed?: boolean;
  limitErrors: string[];
}

const loadImage = async (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (...args) => reject(args);
    img.src = src;
  });

const getImageData = (image: HTMLImageElement) => {
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Could not get 2d context");
  }

  context.drawImage(image, 0, 0);
  return context.getImageData(0, 0, image.width, image.height);
};

export const encodeImageToBlurHash = async (imageUrl: string) => {
  const image = await loadImage(imageUrl);
  const imageData = getImageData(image);
  return encode(imageData.data, imageData.width, imageData.height, 4, 3);
};

const buildVideoThumbnailFallback = (width: number, height: number) => {
  const safeWidth = normalizeMediaDimension(
    width,
    DEFAULT_VIDEO_PREVIEW_DIMENSIONS.width,
  );
  const safeHeight = normalizeMediaDimension(
    height,
    DEFAULT_VIDEO_PREVIEW_DIMENSIONS.height,
  );
  const circleRadius = Math.max(
    48,
    Math.round(Math.min(safeWidth, safeHeight) * 0.18),
  );
  const triangleWidth = Math.round(circleRadius * 0.6);
  const triangleHalfHeight = Math.round(circleRadius * 0.55);
  const centerX = safeWidth / 2;
  const centerY = safeHeight / 2;
  const triangleLeft = Math.round(centerX - triangleWidth * 0.35);

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${safeWidth} ${safeHeight}">
      <defs>
        <linearGradient id="bg" x1="0%" x2="100%" y1="0%" y2="100%">
          <stop offset="0%" stop-color="#0f172a" />
          <stop offset="100%" stop-color="#1e293b" />
        </linearGradient>
      </defs>
      <rect width="${safeWidth}" height="${safeHeight}" fill="url(#bg)" rx="24" />
      <circle cx="${centerX}" cy="${centerY}" r="${circleRadius}" fill="rgba(255,255,255,0.14)" />
      <polygon
        points="${triangleLeft},${centerY - triangleHalfHeight} ${triangleLeft},${centerY + triangleHalfHeight} ${triangleLeft + triangleWidth},${centerY}"
        fill="#f8fafc"
      />
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

type ResolvedMediaDimensions = {
  width: number;
  height: number;
};

const VIDEO_METADATA_PROBE_BYTES = 4 * 1024 * 1024;
const ISO_BOX_HEADER_BYTES = 8;
const FULL_BOX_HEADER_BYTES = 4;
const FIXED_POINT_ONE = 65536;
const ROTATION_MATRIX_TOLERANCE = 1000;

const ISO_BMFF_VIDEO_EXTENSIONS = new Set(["m4v", "mov", "mp4"]);

const ISO_CONTAINER_BOX_TYPES = new Set(["moov", "trak"]);

const isIsoBmffVideoFile = (file: File) => {
  const type = file.type.toLowerCase();

  if (
    type === "video/mp4" ||
    type === "video/quicktime" ||
    type === "video/x-m4v"
  ) {
    return true;
  }

  const extension = file.name.split(".").pop()?.toLowerCase();

  return extension ? ISO_BMFF_VIDEO_EXTENSIONS.has(extension) : false;
};

const readBoxType = (view: DataView, offset: number) => {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
};

const readUnsignedFixed16 = (view: DataView, offset: number) => {
  return view.getUint32(offset, false) / FIXED_POINT_ONE;
};

const isApproximately = (value: number, expected: number) => {
  return Math.abs(value - expected) <= ROTATION_MATRIX_TOLERANCE;
};

const isSidewaysRotationMatrix = (matrix: number[]) => {
  const [a, b, , c, d] = matrix;

  return (
    isApproximately(a ?? 0, 0) &&
    isApproximately(d ?? 0, 0) &&
    isApproximately(Math.abs(b ?? 0), FIXED_POINT_ONE) &&
    isApproximately(Math.abs(c ?? 0), FIXED_POINT_ONE)
  );
};

const parseTrackHeaderDisplayDimensions = (
  view: DataView,
  contentStart: number,
  contentEnd: number,
): ResolvedMediaDimensions | null => {
  if (contentStart + FULL_BOX_HEADER_BYTES > contentEnd) {
    return null;
  }

  const version = view.getUint8(contentStart);
  let offset = contentStart + FULL_BOX_HEADER_BYTES;

  offset += version === 1 ? 32 : 20;
  offset += 8; // reserved
  offset += 8; // layer, alternate group, volume, reserved

  const matrixStart = offset;
  const dimensionsStart = matrixStart + 36;

  if (dimensionsStart + 8 > contentEnd) {
    return null;
  }

  const matrix = Array.from({ length: 9 }, (_, index) =>
    view.getInt32(matrixStart + index * 4, false),
  );
  const width = readUnsignedFixed16(view, dimensionsStart);
  const height = readUnsignedFixed16(view, dimensionsStart + 4);

  if (!isValidMediaDimension(width) || !isValidMediaDimension(height)) {
    return null;
  }

  return isSidewaysRotationMatrix(matrix)
    ? {
        width: Math.round(height),
        height: Math.round(width),
      }
    : {
        width: Math.round(width),
        height: Math.round(height),
      };
};

const readBox = (view: DataView, offset: number, end: number) => {
  if (offset + ISO_BOX_HEADER_BYTES > end) {
    return null;
  }

  const size32 = view.getUint32(offset, false);
  const type = readBoxType(view, offset + 4);
  let headerSize = ISO_BOX_HEADER_BYTES;
  let size = size32;

  if (size32 === 1) {
    if (offset + 16 > end) {
      return null;
    }

    const largeSize = view.getBigUint64(offset + 8, false);

    if (largeSize > BigInt(Number.MAX_SAFE_INTEGER)) {
      return null;
    }

    size = Number(largeSize);
    headerSize = 16;
  } else if (size32 === 0) {
    size = end - offset;
  }

  if (size < headerSize) {
    return null;
  }

  const declaredEnd = offset + size;
  const contentStart = offset + headerSize;
  const boxEnd = Math.min(declaredEnd, end);

  if (contentStart > boxEnd) {
    return null;
  }

  return {
    contentStart,
    end: boxEnd,
    type,
  };
};

const parseIsoBmffBoxesForDisplayDimensions = (
  view: DataView,
  start: number,
  end: number,
): ResolvedMediaDimensions | null => {
  let offset = start;

  while (offset + ISO_BOX_HEADER_BYTES <= end) {
    const box = readBox(view, offset, end);

    if (!box) {
      break;
    }

    if (box.type === "tkhd") {
      const dimensions = parseTrackHeaderDisplayDimensions(
        view,
        box.contentStart,
        box.end,
      );

      if (dimensions) {
        return dimensions;
      }
    }

    if (ISO_CONTAINER_BOX_TYPES.has(box.type)) {
      const dimensions = parseIsoBmffBoxesForDisplayDimensions(
        view,
        box.contentStart,
        box.end,
      );

      if (dimensions) {
        return dimensions;
      }
    }

    offset = box.end;
  }

  return null;
};

export const getIsoBmffDisplayDimensionsFromBuffer = (
  buffer: ArrayBuffer,
): ResolvedMediaDimensions | null => {
  const view = new DataView(buffer);
  const parsedDimensions = parseIsoBmffBoxesForDisplayDimensions(
    view,
    0,
    view.byteLength,
  );

  if (parsedDimensions) {
    return parsedDimensions;
  }

  for (
    let offset = 4;
    offset + ISO_BOX_HEADER_BYTES <= view.byteLength;
    offset += 1
  ) {
    if (readBoxType(view, offset) !== "tkhd") {
      continue;
    }

    const box = readBox(view, offset - 4, view.byteLength);

    if (box?.type !== "tkhd") {
      continue;
    }

    const dimensions = parseTrackHeaderDisplayDimensions(
      view,
      box.contentStart,
      box.end,
    );

    if (dimensions) {
      return dimensions;
    }
  }

  return null;
};

const readVideoContainerDisplayDimensions = async (
  file: File,
): Promise<ResolvedMediaDimensions | null> => {
  if (!isIsoBmffVideoFile(file)) {
    return null;
  }

  const probeRanges = [
    [0, Math.min(file.size, VIDEO_METADATA_PROBE_BYTES)],
  ] as Array<[number, number]>;

  if (file.size > VIDEO_METADATA_PROBE_BYTES) {
    probeRanges.push([
      Math.max(0, file.size - VIDEO_METADATA_PROBE_BYTES),
      file.size,
    ]);
  }

  for (const [start, end] of probeRanges) {
    const dimensions = getIsoBmffDisplayDimensionsFromBuffer(
      await file.slice(start, end).arrayBuffer(),
    );

    if (dimensions) {
      return dimensions;
    }
  }

  return null;
};

const getVideoElementDimensions = (
  video: HTMLVideoElement,
): ResolvedMediaDimensions | null => {
  const dimensions = {
    width: video.videoWidth,
    height: video.videoHeight,
  };

  return hasValidMediaDimensions(dimensions) ? dimensions : null;
};

export const convertImageToDataURL = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (onloadEvent) => {
      const result = onloadEvent.target?.result;
      if (typeof result !== "string") {
        reject("No result found on FileReader event.");
        return;
      }
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const getImageURLs = async (
  files: File[],
  uploaderPublicKey: string,
): Promise<string[]> => {
  return Promise.all(
    files.map(async (file) => {
      const { ImageURL } = await uploadImage(
        {
          UserPublicKeyBase58Check: uploaderPublicKey,
          file,
        },
        {
          nodeURI: process.env.NEXT_PUBLIC_CORE_DESO_NODE,
        },
      );

      return ImageURL;
    }),
  );
};

export const getVideoURLs = async (
  files: File[],
  uploaderPublicKey: string,
): Promise<string[]> => {
  return Promise.all(
    files.map(async (file) => {
      const { url } = await uploadVideo({
        UserPublicKeyBase58Check: uploaderPublicKey,
        file,
      });

      return url;
    }),
  );
};

const activeMediaUploadControllers = new Map<string, AbortController>();

const createMediaUploadCancelledError = () => {
  const error = new Error("Upload cancelled");
  error.name = "AbortError";
  return error;
};

const isMediaUploadCancelled = (error: unknown) =>
  error instanceof Error && error.name === "AbortError";

const startMediaUploadController = (id: string) => {
  const controller = new AbortController();
  activeMediaUploadControllers.set(id, controller);
  return controller;
};

const clearMediaUploadController = (
  id: string,
  controller: AbortController,
) => {
  if (activeMediaUploadControllers.get(id) === controller) {
    activeMediaUploadControllers.delete(id);
  }
};

export const cancelMediaUpload = (id: string) => {
  const controller = activeMediaUploadControllers.get(id);

  if (!controller) {
    return false;
  }

  controller.abort();
  activeMediaUploadControllers.delete(id);
  return true;
};

type UploadDirectVideoOptions = Partial<tus.UploadOptions> & {
  signal?: AbortSignal;
};

export const uploadDirectVideo = async (
  file: File,
  { signal, ...options }: UploadDirectVideoOptions,
): Promise<string> => {
  if (file.size > 4 * (1024 * 1024 * 1024)) {
    throw new Error("Too large file size");
  }

  if (signal?.aborted) {
    throw createMediaUploadCancelledError();
  }

  return new Promise((resolve, reject) => {
    let upload: tus.Upload | null = null;
    let mediaId: string;
    let settled = false;

    function cleanup() {
      signal?.removeEventListener("abort", abortUpload);
    }

    function settleResolve(url: string) {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      resolve(url);
    }

    function settleReject(error: unknown) {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      reject(error);
    }

    function abortUpload() {
      void upload?.abort(true).catch(() => {});
      settleReject(createMediaUploadCancelledError());
    }

    signal?.addEventListener("abort", abortUpload, { once: true });

    const finalOptions: tus.UploadOptions = {
      endpoint: process.env.NEXT_PUBLIC_UPLOAD_VIDEO_URI,
      chunkSize: 50 * 1024 * 1024, // Required a minimum chunk size of 5MB, here we use 50MB.
      metadata: {
        filename: file.name,
        filetype: file.type,
      },
      ...options,

      onError: async (error: Error) => {
        options?.onError?.(error);
        await upload?.abort(true).catch(() => {});
        settleReject(
          signal?.aborted ? createMediaUploadCancelledError() : error,
        );
      },
      onAfterResponse: (req: tus.HttpRequest, res: tus.HttpResponse) => {
        options?.onAfterResponse?.(req, res);
        return new Promise((resolve) => {
          const mediaIdHeader = res.getHeader("stream-media-id");
          if (mediaIdHeader) {
            mediaId = mediaIdHeader;
          }
          resolve();
        });
      },
      onSuccess: async () => {
        // This make sure the video is encoded and ready to play
        await retry({
          action: () => {
            return fetch(`/client-api/media/exists?mediaId=${mediaId}`).catch(
              () => null,
            );
          },
          checkSuccess: (result) => result?.status === 200,
          maxAttempts: 300,
          delayMs: 2000,
        });
        settleResolve(`https://iframe.videodelivery.net/${mediaId}`);
      },
    };
    // Create and start the upload.
    upload = new tus.Upload(file, finalOptions);

    if (signal?.aborted) {
      abortUpload();
      return;
    }

    upload.start();
  });
};

const rgbToHex = (r: number, g: number, b: number) =>
  "#" +
  [r, g, b]
    .map((x) => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    })
    .join("");

export async function getAverageColor(
  imageObject: HTMLImageElement | HTMLVideoElement | string,
) {
  try {
    let image: HTMLImageElement | HTMLVideoElement;

    // To avoid cross-origin issues, we need to fetch through our optimizer
    if (typeof imageObject === "string") {
      const url = `/_next/image?url=${encodeURI(imageObject)}&w=128&q=75`;
      image = new Image();
      // ts-expect-error we want to override
      image.src = url;
    } else {
      image = imageObject;
    }

    await new Promise((resolve, reject) => {
      if (
        ("complete" in image && image.complete) ||
        ("readyState" in image && image.readyState === 4)
      ) {
        resolve(true);
        return;
      }
      image.onload = resolve;
      image.onerror = reject;
    });

    const color = getColorSync(image);

    if (!color) {
      return undefined;
    }

    const rgb = color.array();

    return {
      rgb,
      hex: color.hex() || rgbToHex(rgb[0], rgb[1], rgb[2]),
    };
  } catch (e) {
    console.error(e);
    return undefined;
  }
}

export const metadataToMediaItem = (
  metadata: ImageMetadata | AudioMetadata | VideoMetadata,
) => {
  console.log({ metadata });
  return {
    id: metadata.url,
    file: new File([], "media-item"), // Placeholder, as we don't have the original file here
    metadata,
    progress: 100,
    uploadFailed: false,
    limitErrors: [],
    duration: (metadata as AudioMetadata | VideoMetadata).duration,
    type: metadata.type!.match(/(image|video|audio)/)![0],
    src:
      (metadata as ImageMetadata | VideoMetadata).thumbnailDataURL ||
      metadata.url!,
  } as MediaItem;
};

export interface ImageMetadata {
  clientId?: string;
  thumbnailDataURL: string;
  width: number;
  height: number;
  orientation: "landscape" | "portrait";
  avgColor?: {
    rgb: number[];
    hex: string;
  };
  size: number;
  url?: string;
  type?: "image" | "inline_image" | "gated_image";
}

export const extractMetadataFromImage = async (
  file: File,
): Promise<ImageMetadata> => {
  const thumbnailDataURL = await convertImageToDataURL(file);
  // We need to load the image to get the width and height
  const image = await loadImage(thumbnailDataURL);
  return {
    thumbnailDataURL,
    width: image.width,
    height: image.height,
    orientation: image.width > image.height ? "landscape" : "portrait",
    avgColor: await getAverageColor(image),
    size: file.size,
  };
};

export type VideoMetadata = {
  clientId?: string;
  thumbnailDataURL: string;
  width?: number;
  height?: number;
  orientation?: "landscape" | "portrait";
  avgColor?: {
    rgb: number[];
    hex: string;
  };
  size: number;
  url?: string;
  duration: number;
  type?: "video" | "gated_video";
};

export const extractMetadataFromVideo = async (
  file: File,
): Promise<VideoMetadata> => {
  const objectUrl = URL.createObjectURL(file);
  const containerDimensionsPromise = readVideoContainerDisplayDimensions(
    file,
  ).catch(() => null);

  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    let duration = 0;

    if (!context) {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not get 2d context"));
      return;
    }

    let settled = false;
    const fallbackTimeout = window.setTimeout(() => {
      finalizeWithFallbackThumbnail();
    }, 10000);

    const cleanup = () => {
      window.clearTimeout(fallbackTimeout);
      URL.revokeObjectURL(objectUrl);
      video.pause();
      video.removeAttribute("src");
      video.load();
    };

    const resolveMetadata = async (thumbnailDataURL: string) => {
      if (settled) {
        return;
      }

      settled = true;
      const videoElementDimensions = getVideoElementDimensions(video);
      cleanup();
      const dimensions =
        (await containerDimensionsPromise) ?? videoElementDimensions;
      const dimensionMetadata = dimensions
        ? {
            ...dimensions,
            orientation:
              dimensions.width > dimensions.height ? "landscape" : "portrait",
          }
        : {};

      resolve({
        thumbnailDataURL,
        duration,
        ...dimensionMetadata,
        // we disable this for now, it's unreliable in some cases
        // avgColor: await getAverageColor(video),
        size: file.size,
      });
    };

    const finalizeWithFallbackThumbnail = () => {
      resolveMetadata(
        buildVideoThumbnailFallback(video.videoWidth, video.videoHeight),
      );
    };

    const captureThumbnail = () => {
      try {
        const width = normalizeMediaDimension(
          video.videoWidth,
          DEFAULT_VIDEO_PREVIEW_DIMENSIONS.width,
        );
        const height = normalizeMediaDimension(
          video.videoHeight,
          DEFAULT_VIDEO_PREVIEW_DIMENSIONS.height,
        );

        canvas.width = width;
        canvas.height = height;
        context.drawImage(video, 0, 0, width, height);

        const thumbnailDataURL = canvas.toDataURL();

        if (!thumbnailDataURL || thumbnailDataURL === "data:,") {
          finalizeWithFallbackThumbnail();
          return;
        }

        resolveMetadata(thumbnailDataURL);
      } catch {
        finalizeWithFallbackThumbnail();
      }
    };

    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    video.addEventListener("loadedmetadata", function () {
      duration = video.duration;
    });

    video.addEventListener(
      "loadeddata",
      () => {
        if (duration <= 1) {
          captureThumbnail();
          return;
        }

        try {
          video.currentTime = Math.min(1, Math.max(duration / 2, 0.1));
        } catch {
          captureThumbnail();
        }
      },
      { once: true },
    );

    video.addEventListener("seeked", captureThumbnail, { once: true });
    video.addEventListener("error", finalizeWithFallbackThumbnail, {
      once: true,
    });

    video.src = objectUrl;
    video.load();
  });
};

export type AudioMetadata = {
  clientId?: string;
  duration: number;
  size: number;
  title: string;
  url?: string;
  type?: "audio" | "gated_audio";
};

export const extractMetadataFromAudio = async (
  file: File,
): Promise<AudioMetadata> => {
  return new Promise((resolve, reject) => {
    const audio = document.createElement("audio");

    audio.addEventListener("loadedmetadata", function () {
      resolve({
        duration: audio.duration,
        size: file.size,
        title: file.name,
      });
    });

    audio.addEventListener("error", (e) => {
      reject(e);
    });

    audio.src = URL.createObjectURL(file);
    audio.load();
  });
};

export const formatVideoDuration = (duration: number) => {
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

export const handleImageUpload = (
  images: MediaItem[],
  onUploadProgress: Dispatch<SetStateAction<MediaItem[]>>,
  uploaderPublicKey: string,
) => {
  for (const item of images) {
    const { file, id, progress } = item;
    if (progress === undefined) {
      const controller = startMediaUploadController(id);

      onUploadProgress((existingImages) =>
        existingImages.map((item) =>
          item.id === id ? { ...item, progress: 0 } : item,
        ),
      );

      getImageURLs([file], uploaderPublicKey)
        .then((urls) => {
          if (controller.signal.aborted) {
            return;
          }

          onUploadProgress((existingImages) =>
            existingImages.map((item) =>
              item.id === id
                ? {
                    ...item,
                    metadata: {
                      ...item.metadata,
                      url: urls[0],
                    } as ImageMetadata,
                    progress: 100,
                  }
                : item,
            ),
          );
        })
        .catch((e) => {
          if (controller.signal.aborted || isMediaUploadCancelled(e)) {
            return;
          }

          onUploadProgress((existingImages) =>
            existingImages.map((item) =>
              item.id === id
                ? { ...item, progress: 100, uploadFailed: true }
                : item,
            ),
          );
          toast({
            variant: "destructive",
            title: "Error",
            description: `An error happened when uploading your image`,
            details: createToastErrorDetails(e),
          });
        })
        .finally(() => {
          clearMediaUploadController(id, controller);
        });
    }
  }
};

export const handleVideoUpload = async (
  videos: MediaItem[],
  onUploadProgress: Dispatch<SetStateAction<MediaItem[]>>,
) => {
  // Set initial state
  onUploadProgress(videos);

  for (const item of videos) {
    const { file, progress, id } = item;
    if (progress === null) {
      const controller = startMediaUploadController(id);

      uploadDirectVideo(file, {
        signal: controller.signal,
        onProgress(bytesUploaded, bytesTotal) {
          if (controller.signal.aborted) {
            return;
          }

          onUploadProgress((prevVideos) => {
            return prevVideos.map((item) =>
              item.id === id
                ? {
                    ...item,
                    // We maximize it here for 99% because we don't want to show 100% until we get back the video URL
                    progress: Math.min(bytesUploaded / (bytesTotal / 100), 99),
                  }
                : item,
            );
          });
        },
      })
        .then((url) => {
          if (controller.signal.aborted) {
            return;
          }

          onUploadProgress((prevVideos) => {
            return prevVideos.map((item) =>
              item.id === id
                ? {
                    ...item,
                    metadata: { ...item.metadata, url } as VideoMetadata,
                    progress: 100,
                  }
                : item,
            );
          });
        })
        .catch((e) => {
          if (isMediaUploadCancelled(e)) {
            return;
          }

          onUploadProgress((prevVideos) => {
            return prevVideos.map((item) =>
              item.id === id
                ? { ...item, progress: 100, uploadFailed: true }
                : item,
            );
          });
          toast({
            variant: "destructive",
            title: "Error",
            description: `An error happened when uploading your video`,
            details: createToastErrorDetails(e),
          });
        })
        .finally(() => {
          clearMediaUploadController(id, controller);
        });
    }
  }
};

export const handleAudioUpload = async (
  audios: MediaItem[],
  onUploadProgress: Dispatch<SetStateAction<MediaItem[]>>,
) => {
  // Set initial state
  onUploadProgress(audios);

  for (const item of audios) {
    const { file, progress, id } = item;
    if (progress === null) {
      const controller = startMediaUploadController(id);

      // We immediately set progress to 0, so we will have loading state
      // even if the script is loading in the background
      onUploadProgress((prevAudios) => {
        return prevAudios.map((item) =>
          item.id === id
            ? {
                ...item,
                progress: 0,
              }
            : item,
        );
      });

      void (async () => {
        try {
          const { convertAudioToVideo } =
            await import("@/utils/audio-to-video");

          if (controller.signal.aborted) {
            return;
          }

          const videoBlob = await convertAudioToVideo(file);

          if (controller.signal.aborted) {
            return;
          }

          const url = await uploadDirectVideo(
            new File([videoBlob], "audio.mp4"),
            {
              signal: controller.signal,
              onProgress(bytesUploaded, bytesTotal) {
                if (controller.signal.aborted) {
                  return;
                }

                onUploadProgress((prevAudios) => {
                  return prevAudios.map((item) =>
                    item.id === id
                      ? {
                          ...item,
                          // We maximize it here for 99% because we don't want to show 100% until we get back the video URL
                          progress: Math.min(
                            bytesUploaded / (bytesTotal / 100),
                            99,
                          ),
                        }
                      : item,
                  );
                });
              },
            },
          );

          if (controller.signal.aborted) {
            return;
          }

          onUploadProgress((prevAudios) => {
            return prevAudios.map((item) =>
              item.id === id
                ? {
                    ...item,
                    metadata: { ...item.metadata, url } as AudioMetadata,
                    progress: 100,
                  }
                : item,
            );
          });
        } catch (e) {
          if (isMediaUploadCancelled(e)) {
            return;
          }

          onUploadProgress((prevAudios) => {
            return prevAudios.map((item) =>
              item.id === id
                ? { ...item, progress: 100, uploadFailed: true }
                : item,
            );
          });
          toast({
            variant: "destructive",
            title: "Error",
            description: `An error happened when uploading your video`,
            details: createToastErrorDetails(e),
          });
        } finally {
          clearMediaUploadController(id, controller);
        }
      })();
    }
  }
};

export const extractURLs = (mediaItems: MediaItem[]) => {
  return mediaItems.reduce<string[]>((urls, media) => {
    if (media.metadata?.url) {
      urls.push(media.metadata.url);
    }
    return urls;
  }, []);
};

export const fetchAndEncodeImage = async (
  imageUrl: string,
): Promise<string> => {
  try {
    const response = await axios.get(imageUrl, { responseType: "arraybuffer" });

    const contentType = response.headers["content-type"] || "image/jpeg";
    const base64Image = Buffer.from(response.data).toString("base64");

    return `data:${contentType};base64,${base64Image}`;
  } catch (error) {
    console.error(`Failed to fetch image from ${imageUrl}:`, error);
    return Promise.resolve("");
  }
};

export const isMediaUploading = (mediaItem: MediaItem) => {
  return (
    mediaItem.progress === null ||
    (mediaItem.progress !== undefined && mediaItem.progress < 100)
  );
};
